export type {
  CheckInformationBarrierRequest,
  CheckInformationBarrierResponse,
  CreateInformationBarrierRequest,
  DeleteInformationBarrierResponse,
  InformationBarrier,
  InformationBarrierResponse,
  InformationBarrierScope,
  InformationBarrierSweepStatus,
  ListInformationBarriersResponse,
  UpdateInformationBarrierRequest,
} from '@dreamai/types';
import type {
  InformationBarrier,
  InformationBarrierScope,
  OrganisationMember,
} from '@dreamai/types';

/** How often the list is re-read while a barrier's memory sweep is still applying. */
export const SWEEP_POLL_INTERVAL_MS = 5000;

export const ENCRYPTION_NOT_CONFIGURED_CODE =
  'information_barriers_encryption_not_configured';

export const ENCRYPTION_NOT_CONFIGURED_MESSAGE =
  'Information barriers aren’t available on this deployment yet.';

const CODE_MESSAGES: Record<string, string> = {
  information_barrier_instruction_required: 'Say what should stay out.',
  information_barrier_exemption_unknown:
    'One of the people exempted is no longer a member of this organisation.',
  information_barrier_not_found: 'That barrier no longer exists.',
  [ENCRYPTION_NOT_CONFIGURED_CODE]: ENCRYPTION_NOT_CONFIGURED_MESSAGE,
};

/** Machine-readable reason, whether the backend sent it as `code` or `error`. */
export function informationBarrierErrorCode(err: unknown): string | undefined {
  const data = (
    err as { response?: { data?: { code?: string; error?: string } } }
  )?.response?.data;
  return data?.code ?? data?.error;
}

export function informationBarrierErrorMessage(
  err: unknown,
  fallback: string,
): string {
  const code = informationBarrierErrorCode(err);
  if (code && CODE_MESSAGES[code]) return CODE_MESSAGES[code];
  return fallback;
}

export function isEncryptionNotConfigured(err: unknown): boolean {
  return informationBarrierErrorCode(err) === ENCRYPTION_NOT_CONFIGURED_CODE;
}

/** Same limit the backend enforces on `instruction`. */
export const INSTRUCTION_MAX_LENGTH = 2000;

/** The sweep that follows a create / edit / re-enable is still running. */
export function isApplying(barrier: InformationBarrier): boolean {
  return barrier.sweepStatus === 'pending' || barrier.sweepStatus === 'running';
}

/** The sweep stopped without finishing; switching the barrier off and on re-runs it. */
export function sweepFailed(barrier: InformationBarrier): boolean {
  return barrier.sweepStatus === 'failed';
}

/** People who can be exempted: anyone with a Personal Assistant who hasn't declined or been deleted. */
export function exemptableMembers(
  members: OrganisationMember[],
): OrganisationMember[] {
  return members.filter(
    (m) =>
      m.seatKind === 'assistant' &&
      m.status !== 'declined' &&
      (m.status === 'invited' || !!m.userId),
  );
}

/** Exemptions that still point at someone in the organisation; people who left drop off. */
export function currentExemptions(
  scope: InformationBarrierScope,
  members: OrganisationMember[],
): string[] {
  const ids = new Set(exemptableMembers(members).map((m) => m.id));
  return exemptionsOf(scope).filter((id) => ids.has(id));
}

export function memberLabel(member: OrganisationMember): string {
  return member.name || 'Unnamed member';
}

/** "Sun 4 Oct, 11:02" */
export function formatCreatedAt(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const day = date.toLocaleDateString('en-GB', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
  });
  const time = date.toLocaleTimeString('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
  });
  return `${day.replace(',', '')}, ${time}`;
}

/** Everyone, minus the exempted memberships; an empty list is just everyone. */
export function scopeFor(exemptIds: string[]): InformationBarrierScope {
  return exemptIds.length === 0
    ? { kind: 'everyone' }
    : { kind: 'everyone_except', membershipIds: exemptIds };
}

export function exemptionsOf(scope: InformationBarrierScope): string[] {
  return scope.kind === 'everyone_except' ? scope.membershipIds : [];
}

/** Replaces the barrier with the same id, or prepends it when it is new. */
export function upsertBarrier(
  list: InformationBarrier[],
  next: InformationBarrier,
): InformationBarrier[] {
  const index = list.findIndex((b) => b.id === next.id);
  if (index === -1) return [next, ...list];
  return list.map((b, at) => (at === index ? next : b));
}
